import { useCallback, useEffect, useState } from "react";
import { ghApi } from "../api/api";
import { bookingStayWindow, roomChangeWindow } from "../utils/stayWindow";

/* Rooms that can be offered as a change-room/add-room target for `booking`.
   Checked across every guest house the admin is authorized for (not just the
   booking's primary house) so Add Room / Change Room can pull in a room from
   a different guest house.

   Availability comes straight from the backend's `available-rooms` endpoint,
   which applies the same status + date-overlap rule as the `_check_availability`
   guard that change-room/add-room POSTs run — so the dropdown offers exactly
   what the API will accept. Deriving it client-side instead is what broke
   before: the old cross-check keyed only off "no checkout and not a no-show",
   ignoring booking status and dates, so a cancelled booking's leftover
   assignments (cancel keeps them for audit history) hid those rooms forever.

   That "exactly what the API will accept" only holds if the *window* matches
   too. Add Room is checked against the booking's whole stay, which is the
   default here. Change Room is not: `change_room` checks the window the room
   being vacated actually occupies, which can start later and end earlier than
   the booking's own. Change-room callers must therefore pass `forAssignment` —
   without it, a room that frees up after an early departure is hidden from a
   dropdown whose POST would have accepted it. `window` overrides both, for
   asking about an arbitrary slice (re-seating a freed room part-way through an
   indent).

   `forAssignment` also narrows the *exemption*: only that stay is excused, not
   the whole booking. One booking can hold the same room twice now, so a sibling
   stay is a genuine conflict and must keep the room out of the dropdown. */
export default function useVacantRooms(
  guestHouses,
  booking,
  enabled = true,
  { forAssignment = null, window: windowOverride = null } = {},
) {
  const [vacantRooms, setVacantRooms] = useState([]);
  const [vacancyLoading, setVacancyLoading] = useState(false);

  /* Datetimes, not dates: a same-day booking's window is only well-formed once
     its hours are included (see utils/stayWindow). Both are plain strings, so
     swapping the assignment refetches through refetchVacancy's deps. */
  const { start, end } =
    windowOverride ||
    (forAssignment ? roomChangeWindow(booking, forAssignment) : bookingStayWindow(booking));
  const excludeAssignment = forAssignment?.id || null;

  const refetchVacancy = useCallback(async () => {
    /* The endpoint 400s unless start < end, which would silently blank the
       dropdown — treat an unusable range as "nothing to offer" up front. */
    if (!guestHouses.length || !start || !end || start >= end) {
      setVacantRooms([]);
      return;
    }
    setVacancyLoading(true);
    try {
      const results = await Promise.all(
        guestHouses.map((gh) =>
          ghApi
            .availableRooms(gh.id, start, end,
              excludeAssignment ? { excludeAssignment } : {})
            .then((data) =>
              (data.rooms || []).map((r) => ({
                ...r,
                guestHouseId: gh.id,
                guestHouseName: gh.name,
              })),
            )
            .catch(() => []), /* one GH failing shouldn't blank out the rest */
        ),
      );
      setVacantRooms(results.flat());
    } catch {
      /* Can't confirm availability — offer nothing rather than risk a
         double-booking by showing rooms as vacant. */
      setVacantRooms([]);
    } finally {
      setVacancyLoading(false);
    }
  }, [guestHouses, start, end, excludeAssignment]);

  /* `enabled` lets a caller (BookingDetailModal's Change Room dialog) defer
     the fetch until it's actually needed, instead of firing it on every
     detail-view open — FrontDeskModal always wants it eagerly, so it leaves
     `enabled` at its default. */
  useEffect(() => {
    if (enabled) refetchVacancy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchVacancy, enabled]);

  const newRoomOptions = vacantRooms
    .map((r) => ({
      id: r.id,
      roomNumber: r.room_number,
      roomType: r.room_type_name,
      guestHouseId: r.guestHouseId,
      guestHouseName: r.guestHouseName,
    }))
    .sort(
      (a, b) =>
        a.guestHouseName.localeCompare(b.guestHouseName) ||
        a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true }),
    );

  const newRoomOptionsByGh = new Map();
  newRoomOptions.forEach((r) => {
    if (!newRoomOptionsByGh.has(r.guestHouseId)) newRoomOptionsByGh.set(r.guestHouseId, []);
    newRoomOptionsByGh.get(r.guestHouseId).push(r);
  });

  return { vacantRooms, vacancyLoading, refetchVacancy, newRoomOptions, newRoomOptionsByGh };
}
